import * as React from "react"
import { createPortal } from "react-dom"
import { Control, Emitter } from 'rete'
import { EventsTypes } from "rete/types/events"
import {
    CustomQuery,
    normalizeOsmCustomQueries,
} from "./osm_custom_query"

interface OSMKeyValueControlProps {
    emitter: Emitter<EventsTypes> | null
    getQueries: () => CustomQuery[]
    setQueries: (queries: CustomQuery[]) => void
    nodeId: string
    label?: string
}

// Common OSM keys for autocomplete
const COMMON_OSM_KEYS = [
    "landuse", "amenity", "leisure", "natural", "building", "highway", "waterway",
    "aeroway", "boundary", "man_made", "place", "power", "railway", "shop",
    "tourism", "barrier", "craft", "emergency", "geological", "historic",
    "military", "office", "public_transport", "sport", "telecom", "traffic_sign"
]

// Common OSM key-value pairs for autocomplete
const COMMON_OSM_VALUES: Record<string, string[]> = {
    "landuse": [
        "allotments", "brownfield", "cemetery", "commercial", "construction", "farmland", "farmyard",
        "forest", "greenfield", "greenhouse_horticulture", "industrial", "landfill", "meadow",
        "military", "orchard", "plant_nursery", "quarry", "railway", "recreation_ground",
        "religious", "residential", "retail", "salt_pond", "village_green", "vineyard"
    ],
    "amenity": [
        "college", "hospital", "parking", "school", "university", "restaurant", "cafe", "bar",
        "pub", "fast_food", "fuel", "bank", "atm", "pharmacy", "hospital", "clinic",
        "dentist", "veterinary", "library", "theatre", "cinema", "community_centre",
        "place_of_worship", "fire_station", "police", "post_office", "townhall"
    ],
    "leisure": [
        "garden", "garages", "park", "playground", "pitch", "sports_centre", "stadium",
        "swimming_pool", "fitness_centre", "golf_course", "marina", "beach_resort",
        "water_park", "amusement_arcade", "bowling_alley", "dance", "escape_game",
        "hackerspace", "ice_rink", "miniature_golf", "outdoor_seating", "sauna"
    ],
    "natural": [
        "tree", "wood", "scrub", "heath", "moor", "grassland", "fell", "bare_rock",
        "scree", "shingle", "sand", "water", "wetland", "bay", "beach", "coastline",
        "cave_entrance", "cliff", "peak", "ridge", "saddle", "valley", "volcano",
        "spring", "geyser", "hot_spring", "mud", "sinkhole"
    ],
    "building": [
        "yes", "house", "residential", "apartments", "commercial", "industrial", "retail",
        "warehouse", "office", "school", "hospital", "church", "mosque", "temple",
        "synagogue", "cathedral", "chapel", "civic", "public", "garage", "garages",
        "barn", "farm", "farm_auxiliary", "greenhouse", "hut", "shed", "roof",
        "bridge", "bunker", "ruins", "construction", "stadium", "train_station"
    ],
    "highway": [
        "motorway", "trunk", "primary", "secondary", "tertiary", "unclassified", "residential",
        "service", "motorway_link", "trunk_link", "primary_link", "secondary_link",
        "tertiary_link", "living_street", "pedestrian", "track", "bus_guideway",
        "path", "cycleway", "footway", "bridleway", "steps", "corridor", "elevator"
    ],
    "waterway": [
        "river", "stream", "canal", "ditch", "drain", "brook", "wadi", "riverbank",
        "dock", "boatyard", "dam", "weir", "waterfall", "rapids", "lock", "tidal_channel"
    ],
    "aeroway": [
        "aerodrome", "apron", "gate", "hangar", "helipad", "heliport", "runway",
        "taxiway", "terminal", "windsock"
    ],
    "boundary": [
        "administrative", "national_park", "protected_area", "political", "maritime",
        "postal_code", "census", "ceremonial", "historic", "aboriginal_lands"
    ],
    "man_made": [
        "wastewater_plant", "pipeline", "tower", "lighthouse", "windmill", "water_tower",
        "mast", "communications_tower", "pier", "breakwater", "groyne", "dyke",
        "bridge", "tunnel", "cutline", "embankment", "mineshaft", "adit", "well",
        "petroleum_well", "storage_tank", "silo", "works", "water_works", "wastewater_plant"
    ],
    "shop": [
        "supermarket", "convenience", "mall", "department_store", "bakery", "butcher",
        "seafood", "cheese", "wine", "alcohol", "beverages", "organic", "farm",
        "confectionery", "tea", "coffee", "ice_cream", "chocolate", "frozen_food",
        "pet", "toys", "sports", "outdoor", "clothes", "shoes", "jewelry", "watches",
        "bag", "fashion_accessories", "lingerie", "underwear", "boutique", "fabric",
        "tailor", "chemist", "cosmetics", "hairdresser", "optician", "perfumery",
        "tattoo", "beauty", "massage", "car", "car_repair", "car_parts", "bicycle",
        "bicycle_repair", "motorcycle", "mobile_phone", "computer", "electronics",
        "hifi", "camera", "photo", "video", "video_games", "books", "stationery",
        "gift", "art", "craft", "frame", "collector", "trophy", "carpet", "curtain",
        "interior_decoration", "furniture", "kitchen", "bathroom_furnishing", "bed",
        "houseware", "candles", "lighting", "flooring", "paint", "hardware", "trade",
        "doityourself", "household_linen", "kitchenware", "tiles", "window_blind",
        "fireplace", "security", "energy", "electrical", "gas", "water_supply",
        "funeral_directors", "florist", "garden_centre", "garden_furniture", "agrarian",
        "farm", "laundry", "dry_cleaning", "e-cigarette", "hunting", "fishing",
        "military_surplus", "weapons", "medical_supply", "tobacco", "newsagent",
        "ticket", "lottery", "pawnbroker", "charity", "second_hand", "variety",
        "general", "kiosk", "mall", "marketplace", "baby_goods", "bag", "boutique",
        "cannabis", "copyshop", "dairy", "deli", "dive", "fabric", "frozen_food",
        "funeral_directors", "gas", "hairdresser", "hearing_aids", "herbalist",
        "houseware", "interior_decoration", "jewelry", "kiosk", "kitchen", "laundry",
        "lighting", "locksmith", "massage", "medical_supply", "mobile_phone",
        "money_lender", "motorcycle", "musical_instrument", "newsagent", "optician",
        "outdoor", "paint", "pastry", "pawnbroker", "perfumery", "pet", "photo",
        "radiotechnics", "religion", "scuba_diving", "seafood", "second_hand",
        "sewing", "shoes", "sports", "stationery", "tattoo", "tea", "tiles",
        "tobacco", "toys", "trade", "travel_agency", "tyres", "vacuum_cleaner",
        "variety", "video", "video_games", "watches", "water_supply", "weapons",
        "wholesale", "wine", "window_blind"
    ],
    "tourism": [
        "attraction", "museum", "gallery", "zoo", "theme_park", "aquarium", "artwork",
        "information", "hotel", "motel", "hostel", "guest_house", "apartment", "camp_site",
        "caravan_site", "chalet", "alpine_hut", "wilderness_hut", "picnic_site",
        "viewpoint", "yes"
    ],
    "railway": [
        "rail", "subway", "light_rail", "tram", "monorail", "funicular", "narrow_gauge",
        "preserved", "miniature", "construction", "disused", "abandoned", "platform",
        "station", "halt", "tram_stop", "subway_entrance", "level_crossing", "crossing",
        "signal", "switch", "derail", "buffer_stop", "turntable", "roundhouse", "traverser",
        "wash", "workshop", "locomotive_depot", "yard", "service_station", "depot"
    ]
}

// Component to render dropdown in a portal so it can overflow the modal
const KeyDropdownPortal = ({ suggestions, inputElement, onSelect, onClose }: { 
    suggestions: string[]
    inputElement: HTMLElement | null
    onSelect: (suggestion: string) => void
    onClose: () => void
}) => {
    const [position, setPosition] = React.useState<{ top: number; left: number; width: number } | null>(null)
    const dropdownRef = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => {
        if (inputElement) {
            const updatePosition = () => {
                const rect = inputElement.getBoundingClientRect()
                setPosition({
                    top: rect.bottom + 2,
                    left: rect.left,
                    width: rect.width
                })
            }
            updatePosition()
            // Update position on scroll/resize
            window.addEventListener('scroll', updatePosition, true)
            window.addEventListener('resize', updatePosition)
            return () => {
                window.removeEventListener('scroll', updatePosition, true)
                window.removeEventListener('resize', updatePosition)
            }
        }
    }, [inputElement])

    React.useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node
            if (
                inputElement && 
                !inputElement.contains(target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(target)
            ) {
                onClose()
            }
        }
        // Use a slight delay to allow mousedown on dropdown to register first
        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside)
        }, 10)
        return () => {
            clearTimeout(timeoutId)
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [inputElement, onClose])

    if (!position || !inputElement) return null

    const handleSelect = (suggestion: string) => {
        onSelect(suggestion)
        // Small delay to ensure the select happens before closing
        setTimeout(() => {
            onClose()
        }, 10)
    }

    const dropdown = (
        <div 
            ref={dropdownRef}
            className="list-group border rounded shadow"
            style={{ 
                position: 'fixed',
                zIndex: 1060, // Higher than modal z-index (1050)
                maxHeight: '200px', 
                overflowY: 'auto',
                width: `${position.width}px`,
                top: `${position.top}px`,
                left: `${position.left}px`,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                borderColor: 'rgba(255, 255, 255, 0.3)',
                boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.25)"
            }}
            onMouseDown={(e) => {
                // Prevent closing when clicking inside dropdown
                e.stopPropagation()
            }}
        >
            {suggestions.map((suggestion, i) => (
                <button
                    key={i}
                    type="button"
                    className="list-group-item list-group-item-action"
                    onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleSelect(suggestion)
                    }}
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleSelect(suggestion)
                    }}
                    style={{ 
                        fontSize: '0.875rem', 
                        padding: '0.25rem 0.5rem', 
                        cursor: 'pointer',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'
                    }}
                >
                    {suggestion}
                </button>
            ))}
        </div>
    )

    return createPortal(dropdown, document.body)
}

// Component to render value dropdown in a portal
const ValueDropdownPortal = ({ suggestions, inputElement, onSelect, onClose }: { 
    suggestions: string[]
    inputElement: HTMLInputElement | null | undefined
    onSelect: (suggestion: string) => void
    onClose: () => void
}) => {
    const [position, setPosition] = React.useState<{ top: number; left: number; width: number } | null>(null)
    const dropdownRef = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => {
        if (inputElement) {
            const updatePosition = () => {
                const rect = inputElement.getBoundingClientRect()
                setPosition({
                    top: rect.bottom + 2,
                    left: rect.left,
                    width: rect.width
                })
            }
            updatePosition()
            window.addEventListener('scroll', updatePosition, true)
            window.addEventListener('resize', updatePosition)
            return () => {
                window.removeEventListener('scroll', updatePosition, true)
                window.removeEventListener('resize', updatePosition)
            }
        }
    }, [inputElement])

    React.useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node
            if (
                inputElement && 
                !inputElement.contains(target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(target)
            ) {
                onClose()
            }
        }
        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside)
        }, 10)
        return () => {
            clearTimeout(timeoutId)
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [inputElement, onClose])

    if (!position || !inputElement) return null

    const handleSelect = (suggestion: string) => {
        onSelect(suggestion)
        setTimeout(() => {
            onClose()
        }, 10)
    }

    const dropdown = (
        <div 
            ref={dropdownRef}
            className="list-group border rounded shadow"
            style={{ 
                position: 'fixed',
                zIndex: 1060,
                maxHeight: '200px', 
                overflowY: 'auto',
                width: `${position.width}px`,
                top: `${position.top}px`,
                left: `${position.left}px`,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                borderColor: 'rgba(255, 255, 255, 0.3)',
                boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.25)"
            }}
            onMouseDown={(e) => {
                e.stopPropagation()
            }}
        >
            {suggestions.map((suggestion, i) => (
                <button
                    key={i}
                    type="button"
                    className="list-group-item list-group-item-action"
                    onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleSelect(suggestion)
                    }}
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleSelect(suggestion)
                    }}
                    style={{ 
                        fontSize: '0.875rem', 
                        padding: '0.25rem 0.5rem', 
                        cursor: 'pointer',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'
                    }}
                >
                    {suggestion}
                </button>
            ))}
        </div>
    )

    return createPortal(dropdown, document.body)
}

const OSMQueryModal = ({ getQueries, setQueries, nodeId, onClose, buttonRef }: OSMKeyValueControlProps & { onClose: () => void; buttonRef: HTMLButtonElement | null }) => {
    const [nodePosition, setNodePosition] = React.useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null)
    const keyInputRefs = React.useRef<(HTMLInputElement | null)[]>([])
    const valueInputRefs = React.useRef<(HTMLInputElement | null)[]>([])

    React.useEffect(() => {
        // Calculate position when modal opens - use setTimeout to ensure DOM is ready
        const calculatePosition = () => {
            if (!buttonRef) return

            // Try multiple methods to find the node
            let nodeElement: HTMLElement | null = null

            // Method 1: Traverse up from button
            let element: HTMLElement | null = buttonRef
            let attempts = 0
            while (element && attempts < 10) {
                if (element.classList.contains('node')) {
                    nodeElement = element
                    break
                }
                element = element.parentElement
                attempts++
            }

            // Method 2: Try finding by nodeId in data attributes
            if (!nodeElement) {
                const allNodes = document.querySelectorAll('.node')
                for (const node of Array.from(allNodes)) {
                    // Check if this node contains our button
                    if (node.contains(buttonRef)) {
                        nodeElement = node as HTMLElement
                        break
                    }
                }
            }

            if (nodeElement) {
                const rect = nodeElement.getBoundingClientRect()
                const gap = 10 // Small gap between component and modal
                const availableWidth = window.innerWidth - rect.right - gap - 20
                const modalWidth = Math.min(800, Math.max(400, availableWidth))
                
                setNodePosition({
                    top: Math.max(10, rect.top), // Ensure it's not off-screen at top
                    left: rect.right + gap, // Position flush to the right side with small gap
                    width: modalWidth,
                    maxHeight: Math.min(window.innerHeight - rect.top - 20, window.innerHeight * 0.9)
                })
            } else {
                // Fallback: position relative to button
                const buttonRect = buttonRef.getBoundingClientRect()
                setNodePosition({
                    top: Math.max(10, buttonRect.top),
                    left: buttonRect.right + 10,
                    width: Math.min(800, window.innerWidth - buttonRect.right - 30),
                    maxHeight: Math.min(window.innerHeight - buttonRect.top - 20, window.innerHeight * 0.9)
                })
            }
        }

        // Small delay to ensure DOM is ready
        const timeoutId = setTimeout(calculatePosition, 10)
        
        // Recalculate on window resize and scroll
        const handleResize = () => calculatePosition()
        window.addEventListener('resize', handleResize)
        window.addEventListener('scroll', handleResize, true)
        
        return () => {
            clearTimeout(timeoutId)
            window.removeEventListener('resize', handleResize)
            window.removeEventListener('scroll', handleResize, true)
        }
    }, [buttonRef])
    const initialQueries = normalizeOsmCustomQueries(getQueries())
    const [queries, setLocalQueries] = React.useState<CustomQuery[]>(
        initialQueries.length > 0 ? initialQueries : [{ key: "", value: "", type: "way" }]
    )
    const [keyInputs, setKeyInputs] = React.useState<string[]>(queries.map(q => q.key))
    const [valueInputs, setValueInputs] = React.useState<string[]>(queries.map(q => q.value))
    const [typeInputs, setTypeInputs] = React.useState<Array<"node" | "way" | "relation" | "all">>(queries.map(q => q.type))
    const [keySuggestions, setKeySuggestions] = React.useState<string[][]>(queries.map(() => []))
    const [valueSuggestions, setValueSuggestions] = React.useState<string[][]>(queries.map(() => []))
    const [showKeyDropdown, setShowKeyDropdown] = React.useState<boolean[]>(queries.map(() => false))
    const [showValueDropdown, setShowValueDropdown] = React.useState<boolean[]>(queries.map(() => false))

    const addRow = () => {
        setLocalQueries([...queries, { key: "", value: "", type: "way" }])
        setKeyInputs([...keyInputs, ""])
        setValueInputs([...valueInputs, ""])
        setTypeInputs([...typeInputs, "way"])
        setKeySuggestions([...keySuggestions, []])
        setValueSuggestions([...valueSuggestions, []])
        setShowKeyDropdown([...showKeyDropdown, false])
        setShowValueDropdown([...showValueDropdown, false])
    }

    const removeRow = (index: number) => {
        const newQueries = queries.filter((_, i) => i !== index)
        setLocalQueries(newQueries)
        setKeyInputs(keyInputs.filter((_, i) => i !== index))
        setValueInputs(valueInputs.filter((_, i) => i !== index))
        setTypeInputs(typeInputs.filter((_, i) => i !== index))
        setKeySuggestions(keySuggestions.filter((_, i) => i !== index))
        setValueSuggestions(valueSuggestions.filter((_, i) => i !== index))
        setShowKeyDropdown(showKeyDropdown.filter((_, i) => i !== index))
        setShowValueDropdown(showValueDropdown.filter((_, i) => i !== index))
    }

    const updateKey = (index: number, value: string) => {
        const newKeys = [...keyInputs]
        newKeys[index] = value
        setKeyInputs(newKeys)

        // Filter suggestions
        const filtered = COMMON_OSM_KEYS.filter(k => 
            k.toLowerCase().includes(value.toLowerCase())
        )
        const newSuggestions = [...keySuggestions]
        newSuggestions[index] = filtered.slice(0, 10)
        setKeySuggestions(newSuggestions)
        setShowKeyDropdown(showKeyDropdown.map((show, i) => i === index ? value.length > 0 : show))
        
        // Update value suggestions when key changes
        if (value && COMMON_OSM_VALUES[value]) {
            const newValueSuggestions = [...valueSuggestions]
            newValueSuggestions[index] = COMMON_OSM_VALUES[value].slice(0, 15)
            setValueSuggestions(newValueSuggestions)
        } else {
            const newValueSuggestions = [...valueSuggestions]
            newValueSuggestions[index] = []
            setValueSuggestions(newValueSuggestions)
        }
        
        // Clear value when key changes
        const newValues = [...valueInputs]
        newValues[index] = ""
        setValueInputs(newValues)
    }

    const updateValue = (index: number, value: string) => {
        const newValues = [...valueInputs]
        newValues[index] = value
        
        // Get suggestions based on the selected key
        const key = keyInputs[index] || ""
        const suggestions = key && COMMON_OSM_VALUES[key] 
            ? COMMON_OSM_VALUES[key].filter(v => 
                v.toLowerCase().includes(value.toLowerCase())
            ).slice(0, 15)
            : []
        
        const newSuggestions = [...valueSuggestions]
        newSuggestions[index] = suggestions
        setValueSuggestions(newSuggestions)
        
        setValueInputs(newValues)
        setShowValueDropdown(showValueDropdown.map((show, i) => i === index ? value.length > 0 && suggestions.length > 0 : show))
    }

    const updateType = (index: number, type: "node" | "way" | "relation" | "all") => {
        const newTypes = [...typeInputs]
        newTypes[index] = type
        setTypeInputs(newTypes)
    }

    const selectKey = (index: number, key: string) => {
        const newKeys = [...keyInputs]
        newKeys[index] = key
        setKeyInputs(newKeys)
        setShowKeyDropdown(showKeyDropdown.map((show, i) => i === index ? false : show))
    }

    const saveQueries = () => {
        const validQueries: CustomQuery[] = queries.map((_, index) => ({
            key: keyInputs[index] || "",
            value: valueInputs[index] || "",
            type: typeInputs[index] || "way"
        })).filter(q => q.key.trim() !== "")

        setQueries(validQueries)
        onClose()
    }

    const modalId = `osm-custom-query-modal-${nodeId}`

    const modalContent = nodePosition ? (
        <>
            {/* Floating panel */}
            <div 
                style={{ 
                    position: 'fixed',
                    top: `${nodePosition.top}px`,
                    left: `${nodePosition.left}px`,
                    width: `${nodePosition.width}px`,
                    maxHeight: `${nodePosition.maxHeight}px`,
                    zIndex: 1050,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'visible', // Allow dropdowns to overflow
                    background: "rgba(0, 0, 0, 0.5)",
                    color: "white",
                    boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.25)",
                    borderRadius: "4px",
                    border: "solid 3px transparent"
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="d-flex justify-content-between align-items-center p-3 border-bottom" style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }}>
                    <h5 className="mb-0" style={{ color: 'white' }}>Build Query</h5>
                    <button 
                        type="button" 
                        className="btn btn-sm btn-link" 
                        onClick={onClose}
                        style={{ fontSize: '1.5rem', lineHeight: '1', padding: '0.25rem 0.5rem', color: 'rgba(255, 255, 255, 0.7)' }}
                    >
                        &times;
                    </button>
                </div>
                <div style={{ overflowY: 'auto', flex: '1', padding: '1rem', overflow: 'visible' }}>
                        <p className="small mb-3" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Add custom key-value pairs for OSM queries. Leave value empty to match all values for that key.
                        </p>
                        <table className="table table-sm" style={{ color: 'white' }}>
                            <thead>
                                <tr style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }}>
                                    <th style={{ width: '30%', color: 'white', borderColor: 'rgba(255, 255, 255, 0.2)' }}>Key</th>
                                    <th style={{ width: '40%', color: 'white', borderColor: 'rgba(255, 255, 255, 0.2)' }}>Value (optional)</th>
                                    <th style={{ width: '20%', color: 'white', borderColor: 'rgba(255, 255, 255, 0.2)' }}>Type</th>
                                    <th style={{ width: '10%', color: 'white', borderColor: 'rgba(255, 255, 255, 0.2)' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {queries.map((_, index) => (
                                    <tr key={index} style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }}>
                                        <td style={{ position: 'relative', borderColor: 'rgba(255, 255, 255, 0.2)' }}>
                                            <input
                                                ref={(el) => {
                                                    keyInputRefs.current[index] = el
                                                }}
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={keyInputs[index]}
                                                onChange={(e) => updateKey(index, e.target.value)}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)'
                                                    const newShow = [...showKeyDropdown]
                                                    newShow[index] = true
                                                    setShowKeyDropdown(newShow)
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)'
                                                    // Delay closing to allow click on dropdown
                                                    // Check if focus is moving to dropdown
                                                    setTimeout(() => {
                                                        const activeElement = document.activeElement
                                                        const isClickingDropdown = activeElement && 
                                                            (activeElement.classList.contains('list-group-item') ||
                                                             activeElement.closest('.list-group'))
                                                        if (!isClickingDropdown) {
                                                            const newShow = [...showKeyDropdown]
                                                            newShow[index] = false
                                                            setShowKeyDropdown(newShow)
                                                        }
                                                    }, 150)
                                                }}
                                                placeholder="e.g., landuse"
                                                style={{
                                                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                                    color: 'white',
                                                    borderColor: 'rgba(255, 255, 255, 0.3)'
                                                }}
                                            />
                                            {showKeyDropdown[index] && keySuggestions[index].length > 0 && (
                                                <KeyDropdownPortal
                                                    suggestions={keySuggestions[index]}
                                                    inputElement={keyInputRefs.current[index]}
                                                    onSelect={(suggestion) => selectKey(index, suggestion)}
                                                    onClose={() => {
                                                        const newShow = [...showKeyDropdown]
                                                        newShow[index] = false
                                                        setShowKeyDropdown(newShow)
                                                    }}
                                                />
                                            )}
                                        </td>
                                        <td style={{ position: 'relative', borderColor: 'rgba(255, 255, 255, 0.2)' }}>
                                            <input
                                                ref={(el) => {
                                                    valueInputRefs.current[index] = el
                                                }}
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={valueInputs[index]}
                                                onChange={(e) => updateValue(index, e.target.value)}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)'
                                                    const key = keyInputs[index] || ""
                                                    if (key && COMMON_OSM_VALUES[key]) {
                                                        const suggestions = COMMON_OSM_VALUES[key].slice(0, 15)
                                                        const newSuggestions = [...valueSuggestions]
                                                        newSuggestions[index] = suggestions
                                                        setValueSuggestions(newSuggestions)
                                                        setShowValueDropdown(showValueDropdown.map((show, i) => i === index ? true : show))
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)'
                                                    setTimeout(() => {
                                                        const activeElement = document.activeElement
                                                        const isClickingDropdown = activeElement && 
                                                            (activeElement.classList.contains('list-group-item') ||
                                                             activeElement.closest('.list-group'))
                                                        if (!isClickingDropdown) {
                                                            const newShow = [...showValueDropdown]
                                                            newShow[index] = false
                                                            setShowValueDropdown(newShow)
                                                        }
                                                    }, 150)
                                                }}
                                                placeholder="e.g., residential"
                                                style={{
                                                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                                    color: 'white',
                                                    borderColor: 'rgba(255, 255, 255, 0.3)'
                                                }}
                                            />
                                            {showValueDropdown[index] && valueSuggestions[index].length > 0 && (
                                                <ValueDropdownPortal
                                                    suggestions={valueSuggestions[index]}
                                                    inputElement={valueInputRefs.current[index]}
                                                    onSelect={(suggestion) => {
                                                        const newValues = [...valueInputs]
                                                        newValues[index] = suggestion
                                                        setValueInputs(newValues)
                                                        const newShow = [...showValueDropdown]
                                                        newShow[index] = false
                                                        setShowValueDropdown(newShow)
                                                    }}
                                                    onClose={() => {
                                                        const newShow = [...showValueDropdown]
                                                        newShow[index] = false
                                                        setShowValueDropdown(newShow)
                                                    }}
                                                />
                                            )}
                                        </td>
                                        <td style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }}>
                                            <select
                                                className="form-control form-control-sm"
                                                value={typeInputs[index]}
                                                onChange={(e) => updateType(index, e.target.value as any)}
                                                style={{
                                                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                                    color: 'white',
                                                    borderColor: 'rgba(255, 255, 255, 0.3)'
                                                }}
                                            >
                                                <option value="all" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', color: 'white' }}>All</option>
                                                <option value="node" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', color: 'white' }}>Node</option>
                                                <option value="way" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', color: 'white' }}>Way</option>
                                                <option value="relation" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', color: 'white' }}>Relation</option>
                                            </select>
                                        </td>
                                        <td style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }}>
                                            <button
                                                type="button"
                                                className="btn btn-sm"
                                                onClick={() => removeRow(index)}
                                                disabled={queries.length === 1}
                                                style={{
                                                    backgroundColor: queries.length === 1 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(220, 53, 69, 0.8)',
                                                    color: 'white',
                                                    border: 'none',
                                                    cursor: queries.length === 1 ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                ×
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <button
                            type="button"
                            className="btn btn-sm"
                            onClick={addRow}
                            style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                color: 'white',
                                borderColor: 'rgba(255, 255, 255, 0.3)'
                            }}
                        >
                            + Add Row
                        </button>
                </div>
                <div className="d-flex justify-content-end gap-2 p-3 border-top" style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }}>
                    <button 
                        type="button" 
                        className="btn btn-sm" 
                        onClick={onClose}
                        style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            color: 'white',
                            borderColor: 'rgba(255, 255, 255, 0.3)'
                        }}
                    >
                        Cancel
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-sm" 
                        onClick={saveQueries}
                        style={{
                            backgroundColor: 'rgba(0, 123, 255, 0.8)',
                            color: 'white',
                            borderColor: 'rgba(0, 123, 255, 0.8)'
                        }}
                    >
                        Save Queries
                    </button>
                </div>
            </div>
        </>
    ) : null

    // Use portal to render at document root to avoid positioning issues
    return modalContent ? createPortal(modalContent, document.body) : null
}

class OSMKeyValueErrorBoundary extends React.Component<
    { children: React.ReactNode; fallback: React.ReactNode },
    { hasError: boolean }
> {
    constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError() {
        return { hasError: true }
    }

    render() {
        return this.state.hasError ? this.props.fallback : this.props.children
    }
}

const OSMKeyValueButton = ({ getQueries, setQueries, nodeId }: OSMKeyValueControlProps) => {
    const [showModal, setShowModal] = React.useState(false)
    const buttonRef = React.useRef<HTMLButtonElement>(null)
    const [, forceUpdate] = React.useReducer(x => x + 1, 0)

    // Listen for process events to update display when queries change
    React.useEffect(() => {
        const interval = setInterval(() => {
            forceUpdate()
        }, 500)
        return () => clearInterval(interval)
    }, [])

    const activeQueries = normalizeOsmCustomQueries(getQueries())
    const hasActiveQueries = activeQueries.length > 0

    const removeQuery = (index: number) => {
        const updated = activeQueries.filter((_, i) => i !== index)
        setQueries(updated)
    }

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={(e) => {
                    e.stopPropagation()
                    setShowModal(true)
                }}
                style={{ width: '100%', marginTop: '0.5rem' }}
            >
                Build Query {hasActiveQueries && `(${activeQueries.length})`}
            </button>
            
            {hasActiveQueries && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                    <div style={{ marginBottom: '0.25rem', color: '#6c757d', fontWeight: '500' }}>
                        Active Queries:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        {activeQueries.map((query, index) => {
                            const valueDisplay = (query.value ?? "").trim() ? `=${query.value}` : ""
                            const typeDisplay = query.type === "all" ? "" : ` (${query.type})`
                            return (
                                <div
                                    key={index}
                                    className="d-flex align-items-center justify-content-between"
                                    style={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '4px',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        <code style={{ fontSize: '0.75rem', color: '#ffc107' }}>
                                            {query.key}{valueDisplay}{typeDisplay}
                                        </code>
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-sm p-0"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            removeQuery(index)
                                        }}
                                        style={{
                                            color: '#dc3545',
                                            fontSize: '0.875rem',
                                            lineHeight: '1',
                                            padding: '0.125rem 0.25rem',
                                            marginLeft: '0.5rem'
                                        }}
                                        title="Remove query"
                                    >
                                        ×
                                    </button>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}

            {showModal && (
                <OSMQueryModal
                    getQueries={getQueries}
                    setQueries={setQueries}
                    nodeId={nodeId}
                    emitter={null}
                    onClose={() => setShowModal(false)}
                    buttonRef={buttonRef.current}
                />
            )}
        </>
    )
}

const OSMKeyValueButtonSafe = (props: OSMKeyValueControlProps) => (
    <OSMKeyValueErrorBoundary
        fallback={
            <OSMKeyValueButton
                {...props}
                getQueries={() => []}
            />
        }
    >
        <OSMKeyValueButton {...props} />
    </OSMKeyValueErrorBoundary>
)

export class OSMCustomQueryControl extends Control {
    props: OSMKeyValueControlProps
    component: (props: OSMKeyValueControlProps) => JSX.Element
    type: string

    constructor(emitter: Emitter<EventsTypes> | null, key: string, nodeId: string) {
        super(key)
        this.type = 'OSMCustomQueryControl'

        this.props = {
            emitter,
            label: "Build Query",
            getQueries: () => normalizeOsmCustomQueries(this.getData(key)),
            setQueries: (queries: CustomQuery[]) => {
                this.putData(key, normalizeOsmCustomQueries(queries))
                emitter?.trigger("process")
            },
            nodeId
        }

        this.component = OSMKeyValueButtonSafe
    }
}
